import { uniq } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { Select } from '~/eds/src/components/Select/Select';
import { Option } from '~/eds/src/types/Option';
import { Nullable } from '~/types/Nullable';

interface Props {
  value: Nullable<Option<string>>;
  disabled?: boolean;
  onChange: (value: Nullable<Option<string>>) => void;
}

export const NotApplicableOptionSelect = ({
  value: initialValue,
  disabled = false,
  onChange,
}: Props) => {
  const intl = useIntl();

  const initialOptions = uniq([
    intl.formatMessage({
      id: 'NotApplicableOptionSelect.silent',
      defaultMessage: 'Silent',
      description: 'Option for silent response when data is not found',
    }),
    intl.formatMessage({
      id: 'NotApplicableOptionSelect.notApplicable',
      defaultMessage: 'Not Applicable',
      description: 'Option when the field is not applicable to the document',
    }),
    intl.formatMessage({
      id: 'NotApplicableOptionSelect.notFound',
      defaultMessage: 'Not Found',
      description: 'Option when the data is not found in the document',
    }),
    intl.formatMessage({
      id: 'NotApplicableOptionSelect.requiresHumanReview',
      defaultMessage: 'Requires Human Review',
      description: 'Option when the data requires human review',
    }),
    ...(initialValue ? [initialValue.label] : []),
  ]);
  const [options, setOptions] = useState(initialOptions);
  const [selectedOption, setSelectedOption] = useState<Nullable<string>>(
    initialValue?.value ?? null,
  );
  return (
    <Select<string, false>
      disabled={disabled}
      isMulti={false}
      name="not-applicable-dropdown-option-select"
      options={options.map((option) => ({ value: option, label: option }))}
      value={selectedOption}
      onChange={(newOption) => {
        setSelectedOption(newOption);
        onChange(
          newOption
            ? {
                label: newOption,
                value: newOption,
              }
            : null,
        );
      }}
      onAddOption={(newOption) => setOptions([...options, newOption.label])}
    />
  );
};
